import { DestroyRef, Directive, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, NgControl } from '@angular/forms';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { TIME_TRACKING_TIME_FORMAT } from '@app/constants';
import { Locale } from '@app/types/translatable.type';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { TimeFormatValidator } from './time-format-validator.directive';

@Directive({
    selector: '[appTimeFormat]',
})
export class TimeFormatDirective implements OnInit {
    @Input() matAutocompleteComponent!: MatAutocomplete;
    @Output() valueFormatted = new EventEmitter<string>();
    private destroyRef = inject(DestroyRef);
    private timeFormatValidator: TimeFormatValidator;

    constructor(
        private control: NgControl,
        private translateService: TranslateService
    ) {
        this.timeFormatValidator = new TimeFormatValidator(this.translateService);
    }

    ngOnInit(): void {
        this.matAutocompleteComponent.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.formatValue();
        });
    }

    private formatValue(): void {
        const value = this.control.value;

        if (!value) return;

        const timeFormatError = this.timeFormatValidator.validate({
            value,
        } as AbstractControl);

        if (timeFormatError) {
            const formattedValue = this.formatTime(value);
            if (formattedValue !== value) {
                this.control.control?.setValue(formattedValue, { emitEvent: false });
                this.valueFormatted.emit(formattedValue);
                return;
            }
        }
    }

    private formatTime(value: string): string {
        const digits = value.replace(/\D/g, '');

        if (digits.length === 3 || digits.length === 4) {
            const hours = parseInt(digits.slice(0, -2), 10);
            const minutes = digits.slice(-2);

            if (hours >= 1 && hours <= 12) {
                const period = hours >= 7 && hours <= 11 ? 'AM' : 'PM';
                return moment(`${hours}:${minutes} ${period}`, 'h:mm A').format(
                    TIME_TRACKING_TIME_FORMAT[this.translateService.currentLang as Locale]
                );
            }
        }

        return value;
    }
}
